import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { useCachedMedia } from "../hooks/use-cached-media";
import { cn } from "../lib/utils";

interface VideoPlayerProps {
    url: string;
    className?: string;
    poster?: string;
}

/** Shown in place of a video while the server is still compressing it. */
export const VideoProcessingPlaceholder = ({ className }: { className?: string }) => (
    <div className={cn("flex aspect-video w-full flex-col items-center justify-center gap-2 bg-black text-sm text-white/70", className)}>
        <Loader2 className="h-6 w-6 animate-spin" />
        <span>Video se obrađuje…</span>
    </div>
);

export const VideoPlayer = ({ url, className, poster }: VideoPlayerProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const cachedUrl = useCachedMedia(url);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        video.pause();
                    }
                });
            },
            { threshold: 0.1 }
        );

        observer.observe(video);
        return () => observer.disconnect();
    }, []);

    return (
        <video
            ref={videoRef}
            src={cachedUrl ? `${cachedUrl}#t=0.1` : undefined}
            poster={poster}
            preload="metadata"
            playsInline
            controls
            className={className}
        />
    );
};
